import * as React from "react";
import { cx } from "../internal/cx.js";
import { StepIndicator, type StepIndicatorStep } from "../molecules/step-indicator.js";

export type PaymentLayoutContainer = "default" | "narrower";

export interface PaymentLayoutProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "title" | "onChange"> {
  header?: React.ReactNode;
  footer?: React.ReactNode;
  steps?: StepIndicatorStep[];
  currentStep?: number;
  onStepClick?: (stepId: string, stepIndex: number) => void;
  title?: React.ReactNode;
  description?: React.ReactNode;
  /** Width cap for the focused payment panel. */
  container?: PaymentLayoutContainer;
  /** Security/reassurance line shown under the screen intro. */
  securityNote?: React.ReactNode;
}

/**
 * Page-level shell for guided payment flows. Centers a focused panel
 * with an optional `StepIndicator`, intro block, and security note above
 * the children-driven content area.
 */
export const PaymentLayout = React.forwardRef<HTMLDivElement, PaymentLayoutProps>(
  function PaymentLayout(
    {
      children,
      className,
      container = "default",
      currentStep = 0,
      description,
      footer,
      header,
      onStepClick,
      securityNote = "Secured with bank-level encryption",
      steps,
      title,
      ...props
    },
    ref,
  ) {
    return (
      <div
        ref={ref}
        className={cx(
          "cw-v2-theme",
          "cw-v2-payment-layout",
          `cw-v2-payment-layout--container-${container}`,
          className,
        )}
        {...props}
      >
        {header ? <div className="cw-v2-payment-layout__header">{header}</div> : null}
        <main className="cw-v2-payment-layout__main">
          <div className="cw-v2-payment-layout__flow">
            {steps && steps.length > 0 ? (
              <StepIndicator
                ariaLabel="Payment setup progress"
                className="cw-v2-payment-layout__steps"
                currentStep={currentStep}
                onStepClick={onStepClick}
                steps={steps}
              />
            ) : null}
            {title || description || securityNote ? (
              <header className="cw-v2-payment-layout__intro">
                {title ? <h1 className="cw-v2-payment-layout__title">{title}</h1> : null}
                {description ? (
                  <div className="cw-v2-payment-layout__description">{description}</div>
                ) : null}
                {securityNote ? (
                  <div className="cw-v2-payment-layout__security-note">{securityNote}</div>
                ) : null}
              </header>
            ) : null}
            <div className="cw-v2-payment-layout__panel">
              <div className="cw-v2-payment-layout__content">{children}</div>
            </div>
          </div>
        </main>
        {footer ? <div className="cw-v2-payment-layout__footer">{footer}</div> : null}
      </div>
    );
  },
);
PaymentLayout.displayName = "PaymentLayout";
